import { ReactElement, useState } from 'react';
import {
    Datagrid,
    FunctionField,
    List,
    TextField,
    NumberField,
    useTranslate,
    TopToolbar,
    FilterForm,
    TextInput,
    SelectInput,
    DateInput,
    useListContext,
} from 'react-admin';
import dayjs from 'dayjs';
import {
    Box,
    Button,
    Card,
    Chip,
    Dialog,
    Typography,
} from '@mui/material';
import { CustomBreadcrumbs } from 'src/components/custom-breadcrumbs';
import { DashboardContent } from 'src/layouts/dashboard';
import { Resources } from '../resource';
import { SplashScreen } from 'src/components/loading-screen';
import { EmptyContent } from 'src/components/empty-content';
import { formatMoneyShort } from 'src/utils/format';
import { PermissionGuard } from 'src/routes/components/permission-guard';
import ErrorPage403 from 'src/pages/error/403';
import TxField from 'src/pages/admin/components/TxField';

const SIDE_CHOICES = [
    { id: 'BUY', name: 'BUY' },
    { id: 'SELL', name: 'SELL' },
    { id: 'LONG', name: 'LONG' },
    { id: 'SHORT', name: 'SHORT' },
];

const STATUS_CHOICES = [
    { id: 'FILLED', name: 'FILLED' },
    { id: 'CANCELED', name: 'CANCELED' },
    { id: 'RESTING', name: 'RESTING' },
];

const ListLoadingOverlay = () => {
    const { isLoading } = useListContext();

    if (!isLoading) return null;

    return (
        <Box
            sx={{
                position: 'absolute',
                inset: 0,
                zIndex: 10,
                bgcolor: 'rgba(255,255,255,0.75)',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
            }}
        >
            <SplashScreen />
        </Box>
    );
};

const PerpetualTradingListActions = () => {
    const translate = useTranslate();
    const { setFilters, filterValues, setPage } = useListContext();
    const hasActiveFilters = Object.keys(filterValues ?? {}).length > 0;

    const handleResetFilters = () => {
        setPage(1);
        setFilters({}, {});
    };

    return (
        <TopToolbar
            sx={{
                minHeight: 90,
                alignItems: 'center',
                px: 3,
                pt: 1,
                overflow: 'visible',
                width: '100%',
            }}
        >
            <Box
                sx={{
                    display: 'flex',
                    alignItems: 'center',
                    width: '100%',
                    gap: 2,
                    flexWrap: 'wrap',
                    '& form': {
                        display: 'flex',
                        alignItems: 'center',
                        gap: 2,
                        flexWrap: 'wrap',
                        flex: 1,
                    },
                    '& .ra-input': { mt: 0, mb: 0 },
                    '& .MuiFormControl-root': { mt: 0, mb: 0 },
                    '& .MuiTextField-root': { mt: 0, mb: 0 },
                    '& .MuiInputBase-root': { minHeight: 40 },
                }}
            >
                <FilterForm
                    filters={[
                        <TextInput
                            key="walletAddress"
                            source="walletAddress"
                            label="module.perpetualTrading.walletAddress"
                            alwaysOn
                            resettable
                            size="small"
                            sx={{ minWidth: 220 }}
                        />,
                        <SelectInput
                            key="status"
                            source="status"
                            label="module.perpetualTrading.status"
                            choices={STATUS_CHOICES}
                            alwaysOn
                            resettable
                            emptyText="ALL"
                            size="small"
                            sx={{ minWidth: 180 }}
                        />,
                        <DateInput
                            key="from"
                            source="from"
                            label="common.fromTime"
                            alwaysOn
                            size="small"
                            parse={(value: string) =>
                                value ? dayjs(value).startOf('day').valueOf() : undefined
                            }
                            format={(value: any) =>
                                value ? dayjs(Number(value)).format('YYYY-MM-DD') : ''
                            }
                            sx={{ minWidth: 180 }}
                        />,
                        <DateInput
                            key="to"
                            source="to"
                            label="common.toTime"
                            alwaysOn
                            size="small"
                            parse={(value: string) =>
                                value ? dayjs(value).endOf('day').valueOf() : undefined
                            }
                            format={(value: any) =>
                                value ? dayjs(Number(value)).format('YYYY-MM-DD') : ''
                            }
                            sx={{ minWidth: 180 }}
                        />,
                    ]}
                />
                <Button
                    variant="outlined"
                    size="medium"
                    onClick={handleResetFilters}
                    disabled={!hasActiveFilters}
                >
                    {translate('common.reset')}
                </Button>
            </Box>
        </TopToolbar>
    );
};

const CopyableLink = ({
    value,
    href,
    width = 120,
}: {
    value?: string;
    href?: string;
    width?: number;
}) => {
    if (!value) return null;

    return <TxField id={value} href={href} width={width} />;
};

const PerpetualTradingList = () => {
    const [popup, setPopup] = useState<ReactElement>();
    const translate = useTranslate();

    const sideColor = (side?: string) => {
        const s = String(side || '').toUpperCase();
        if (s === 'BUY' || s === 'LONG') return 'success' as const;
        if (s === 'SELL' || s === 'SHORT') return 'error' as const;
        return 'default' as const;
    };

    return (
        <DashboardContent>
            <PermissionGuard resource="perpetual-trading" action="read" fallback={<ErrorPage403 />}>
                <CustomBreadcrumbs
                    heading={translate('resource.perpetualTrading', { _: 'Perpetual Trading' })}
                    links={[
                        {
                            name: translate('resource.perpetualTrading', { _: 'Perpetual Trading' }),
                            href: `/${Resources.perpetualTrading}`,
                        },
                        { name: translate('resource.list', { _: 'List' }) },
                    ]}
                />

                <Card
                    sx={{
                        mt: 3,
                        pb: 2,
                        '.col-side-center': { textAlign: 'center' },
                        '.col-status-center': { textAlign: 'center' },
                    }}
                >
                    <List
                        actions={<PerpetualTradingListActions />}
                        empty={false}
                        perPage={10}
                        exporter={false}
                        sort={{ field: 'createdAtRaw', order: 'DESC' }}
                    >
                        <Box sx={{ overflow: 'auto', position: 'relative' }}>
                            <ListLoadingOverlay />

                            <Datagrid
                                rowClick={false}
                                empty={<EmptyContent />}
                                bulkActionButtons={false}
                                sx={{
                                    '& .RaDatagrid-tableWrapper': { overflowX: 'auto' },
                                    '& .RaDatagrid-table': { tableLayout: 'auto' },
                                    '& .column-walletAddress': {
                                        minWidth: 260,
                                        wordBreak: 'break-all',
                                    },
                                    '& .column-orderTimeId': {
                                        minWidth: 240,
                                        whiteSpace: 'normal',
                                    },
                                    '& .column-symbol': {
                                        minWidth: 120,
                                        whiteSpace: 'nowrap',
                                    },
                                    '& .column-coin': {
                                        minWidth: 90,
                                        whiteSpace: 'nowrap',
                                    },
                                    '& .column-base': {
                                        minWidth: 90,
                                        whiteSpace: 'nowrap',
                                    },
                                    '.RaBulkActionsToolbar-toolbar': {
                                        position: 'relative !important',
                                        transform: 'none !important',
                                    },
                                }}
                            >
                                <TextField
                                    source="symbol"
                                    label="module.perpetualTrading.symbol"
                                    sortable={false}
                                    headerClassName="column-symbol"
                                    cellClassName="column-symbol"
                                />

                                <TextField
                                    source="coin"
                                    label="module.perpetualTrading.coin"
                                    sortable={false}
                                    headerClassName="column-coin"
                                    cellClassName="column-coin"
                                />

                                {/* <TextField
                  source="base"
                  label="module.perpetualTrading.base"
                  sortable={false}
                  headerClassName="column-base"
                  cellClassName="column-base"
                /> */}

                                <FunctionField
                                    source="side"
                                    label="module.perpetualTrading.side"
                                    sortable={false}
                                    headerClassName="col-side-center"
                                    cellClassName="col-side-center"
                                    render={(record: any) => (
                                        <Chip
                                            label={record?.side ?? '-'}
                                            color={sideColor(record?.side)}
                                            size="small"
                                            sx={{ mx: 'auto' }}
                                        />
                                    )}
                                />

                                <FunctionField
                                    source="avgPrice"
                                    label="module.perpetualTrading.price"
                                    sortable={false}
                                    render={(record: any) => record.avgPrice ? record.avgPrice : (record.price || '-')
                                    }
                                />

                                <NumberField
                                    source="size"
                                    label="module.perpetualTrading.size"
                                    sortable={false}
                                    options={{
                                        minimumFractionDigits: 6,
                                        maximumFractionDigits: 6,
                                    }}
                                />

                                <FunctionField
                                    source="volume"
                                    label="module.perpetualTrading.volume"
                                    sortable={false}
                                    render={(record: any) =>
                                        record?.volume != null ? formatMoneyShort(Number(record.volume)) : '-'
                                    }
                                />

                                <FunctionField
                                    source="buildFee"
                                    label="module.perpetualTrading.buildFee"
                                    sortable={false}
                                    render={(record: any) =>
                                        record?.buildFee != null ? Number(record.buildFee).toFixed(6) : '-'
                                    }
                                />

                                <FunctionField
                                    source="status"
                                    label="module.perpetualTrading.status"
                                    sortable={false}
                                    headerClassName="col-status-center"
                                    cellClassName="col-status-center"
                                    render={(record: any) => (
                                        <Chip
                                            label={record?.status ?? '-'}
                                            color="default"
                                            size="small"
                                            sx={{ mx: 'auto' }}
                                        />
                                    )}
                                />

                                <FunctionField
                                    label="module.perpetualTrading.orderTimeId"
                                    sortable={false}
                                    headerClassName="column-orderTimeId"
                                    cellClassName="column-orderTimeId"
                                    render={(record: any) => (
                                        <Box>
                                            <Typography variant="body2">
                                                {record?.createdAtRaw
                                                    ? dayjs(record.createdAtRaw).format('YYYY-MM-DD HH:mm:ss')
                                                    : '-'}
                                            </Typography>
                                            <Typography variant="caption" color="text.secondary">
                                                {record?.oid ?? '-'}
                                            </Typography>
                                        </Box>
                                    )}
                                />

                                <TextField
                                    source="walletAddress"
                                    label="module.perpetualTrading.walletAddress"
                                    sortable={false}
                                    headerClassName="column-walletAddress"
                                    cellClassName="column-walletAddress"
                                />
                                <FunctionField
                                    label="Hash"
                                    sortable={false}
                                    render={(r: any) => {
                                        const tx = r?.hash;
                                        if (!tx) return '-';
                                        return <CopyableLink value={tx} width={100} />;
                                    }}
                                />
                            </Datagrid>
                        </Box>
                    </List>
                </Card>

                <Dialog open={Boolean(popup)} onClose={() => setPopup(undefined)} maxWidth="md" fullWidth>
                    {popup}
                </Dialog>
            </PermissionGuard>
        </DashboardContent>
    );
};

export default PerpetualTradingList;